import { ThemeProps } from '@kijiji/theme'
import styled from 'styled-components'

import { styleFromTypography } from '@/ui/helpers/styleFromTypography'
import { RequiredPickMap } from '@/ui/typings/helpers'

import { ListItemProps } from './'

type ListItemBaseProps = RequiredPickMap<
  ListItemProps,
  'bulletColor' | 'size' | 'variant' | 'listItemGap' | 'iconSize'
>

/**
 * ListItem 'bullet' variant
 */
type ListItemBulletVariantStyleProps = Pick<
  ListItemBaseProps,
  '$bulletColor' | '$size'
> & {
  theme: ThemeProps
}

const bulletFromSize = {
  xLarge: '0.8rem',
  large: '0.8rem',
  medium: '0.6rem',
  small: '0.6rem',
  xSmall: '0.5rem',
  xxSmall: '0.6rem',
}

const listItemBulletVariantStyle = ({
  $bulletColor,
  $size,
  theme,
}: ListItemBulletVariantStyleProps) => `
  padding-left: calc(
    ${bulletFromSize[$size]} + ${theme.spacing.defaultSmall}
  );
  position: relative;

  &::before {
    background-color: ${$bulletColor};
    border-radius: ${theme.borderRadius.rounded};
    content: '';
    display: inline-block;
    font-weight: bold;
    height: ${bulletFromSize[$size]};
    left: 0;
    position: absolute;
    top: ${$size === 'small' || $size === 'xSmall' || $size === 'xxSmall' ? '.6rem' : '1.2rem'};
    width: ${bulletFromSize[$size]};
  }
`

/**
 * ListItem 'icon' variant
 */
type ListItemIconVariantStyleProps = {
  $listItemGap: string
  $iconSize: string
}

const listItemIconVariantStyle = ({
  $listItemGap,
  $iconSize,
}: ListItemIconVariantStyleProps) => `
  align-items: center;
  display: flex;
  position: relative;

  & svg {
    flex-shrink: 0;
    height: auto;
    width: ${$iconSize};
  }

  > span {
    padding-left: ${$listItemGap};
  }
`

/**
 * ListItem 'base'
 */

export const ListItemBase = styled.li<ListItemBaseProps>(
  ({ $bulletColor, $size, $variant, theme, $listItemGap, $iconSize }) => `
  ${styleFromTypography(theme.typography.body[$size])}

  list-style: none;
  flex-shrink: 0;

  ${
    $variant === 'bullet'
      ? listItemBulletVariantStyle({ $bulletColor, $size, theme })
      : ''
  }
  ${$variant === 'icon' ? listItemIconVariantStyle({ $iconSize, $listItemGap }) : ''}
`
)
